import { FulfillmentIntegrationName } from '@caresend/types';
import { getUnknownErrorMessage } from '@caresend/utils';
import { keepParams, keepQuery } from '../../../../router/helpers/builders.js';
import { getRouter, getRoute } from '../../../../router/helpers/initRouter.js';
import { buildMaybePartnerRouteWithCurrentRoute } from '../../../../router/helpers/router.js';
import { SchedulingFlowRouteName } from '../../../../router/routes/schedulingFlow/model.js';
import { getStore } from '../../../helpers/initStore.js';

/**
 * This is the error message that is returned when the user is scheduling an
 * exam in center and something wrong happens.
 */
const EXAMONE_IN_CENTER_ERROR_MESSAGE = 'Request failed with status code 400';
const getIsExamErrorInCenterError = (errorMessage) => {
    const isExamErrorInCenterError = errorMessage.includes(EXAMONE_IN_CENTER_ERROR_MESSAGE);
    return isExamErrorInCenterError;
};
const sliceConfirmVisitErrorMessage = (errorMessage) => {
    const SCHEDULE_DRAFT_WAYPOINT_ERROR_PREFIX = '[ScheduleDraftWaypointVisit]';
    const indexOfPrefix = errorMessage
        .indexOf(SCHEDULE_DRAFT_WAYPOINT_ERROR_PREFIX);
    if (indexOfPrefix === -1)
        return null;
    return errorMessage
        .slice(indexOfPrefix + SCHEDULE_DRAFT_WAYPOINT_ERROR_PREFIX.length)
        .trim();
};
const navToNextStepOrExit = () => {
    const store = getStore();
    const router = getRouter();
    const route = getRoute();
    const nextStepRoute = store.getters['schedulingFlow/getNextStepRouteInOrder'](route.value);
    if (nextStepRoute) {
        router.replace(nextStepRoute);
        return;
    }
    store.dispatch('schedulingFlow/maybeRouteToExitRoute');
};
/**
 * This function is used to confirm the visit. This is defined here because the
 * logic is used for the `SchedulingFlowCheckout` and `SchedulingPayment`
 * components and the checkout's completion action. See
 * `.../schedulingFlow/routes.ts` for more details on why.
 *
 * If there is an error with the exam one in-center confirmation call,
 * we need to navigate back to the facility time page to allow the user to select a new time.
 */
const confirmVisit = async () => {
    const store = getStore();
    const router = getRouter();
    const route = getRoute();
    try {
        await store.dispatch('schedulingFlow/confirmVisit');
    }
    catch (error) {
        const { selectedDrawPlaceID } = store.state.schedulingFlow;
        const isExamOneInCenter = !!selectedDrawPlaceID
            && store.getters['schedulingFlow/getLabHasFulfillmentIntegration'](selectedDrawPlaceID, FulfillmentIntegrationName.EXAMONE);
        const isQuestInCenterError = !!selectedDrawPlaceID
            && store.getters['schedulingFlow/getLabHasFulfillmentIntegration'](selectedDrawPlaceID, FulfillmentIntegrationName.QUEST);
        const unknownErrorMessage = getUnknownErrorMessage(error);
        const slicedErrorMessage = sliceConfirmVisitErrorMessage(unknownErrorMessage);
        const isExamErrorInCenterError = isExamOneInCenter
            && getIsExamErrorInCenterError(unknownErrorMessage);
        let errorMessage;
        if (slicedErrorMessage) {
            // We have a double booking attempt in this situation
            // We reset scheduling options to ensure `schedulingFlow/getSchedulingOptions`
            // fetches new options.
            store.commit('schedulingFlow/RESET_SCHEDULING_OPTIONS');
            errorMessage = slicedErrorMessage;
        }
        else if (isExamErrorInCenterError || isQuestInCenterError) {
            // Error handling for lab integration confirmation errors
            // Used to ensure that the next fetch will be triggered.
            store.commit('schedulingFlow/RESET_LAB_APPOINTMENT_OPTIONS_FETCH', selectedDrawPlaceID);
            errorMessage = 'There was an error scheduling your facility visit. Please select a new time and try again.';
            const facilityTimePage = buildMaybePartnerRouteWithCurrentRoute({ name: SchedulingFlowRouteName.SCHEDULING_FLOW_FACILITY_TIME }, route.value);
            const backPage = keepParams(keepQuery(facilityTimePage));
            router.push(backPage);
        }
        else {
            errorMessage = 'Visit could not be scheduled.';
        }
        throw Error(errorMessage);
    }
};

export { confirmVisit, navToNextStepOrExit };
